import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  InputAdornment,
  Skeleton,
  Paper,
  MenuItem,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Edit,
  Delete,
  Add,
  Refresh,
  Bolt,
  Upload,
  Download,
  CheckCircle,
  AutoAwesome,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface VSQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswerIndex: number;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
  timesAsked: number;
  correctAnswerRate: number;
  createdAt: Date;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const VSQuestionsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<VSQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<VSQuestion | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<VSQuestion | null>(null);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [tabValue, setTabValue] = useState(0);
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [jobStatus, setJobStatus] = useState<any>(null);

  // Form states
  const [formData, setFormData] = useState({
    question: '',
    option1: '',
    option2: '',
    option3: '',
    option4: '',
    correctAnswerIndex: 0,
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
    category: 'general',
  });

  const categories = [
    { value: 'general', label: 'Genel Kültür' },
    { value: 'coffee', label: 'Kahve Bilgisi' },
    { value: 'history', label: 'Tarih' },
    { value: 'science', label: 'Bilim' },
    { value: 'geography', label: 'Coğrafya' },
    { value: 'sports', label: 'Spor' },
    { value: 'art', label: 'Sanat' },
    { value: 'music', label: 'Müzik' },
    { value: 'movies', label: 'Film & Dizi' },
    { value: 'food', label: 'Yemek & İçecek' },
  ];

  useEffect(() => {
    fetchQuestions();
    fetchJobStatus();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.get(`${API_BASE_URL}/api/vs/questions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const questionsList = response.data.data.questions.map((q: any) => ({
          ...q,
          createdAt: q.createdAt ? new Date(q.createdAt) : new Date(),
        }));
        setQuestions(questionsList);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setSnackbar({ open: true, message: 'Sorular yüklenirken hata oluştu', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchJobStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/daily-job-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setJobStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
    }
  };

  const handleAddQuestion = async () => {
    if (!formData.question || !formData.option1 || !formData.option2 || !formData.option3 || !formData.option4) {
      setSnackbar({ open: true, message: 'Lütfen tüm alanları doldurun', severity: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');

      await axios.post(
        `${API_BASE_URL}/api/vs/questions`,
        {
          question: formData.question,
          options: [formData.option1, formData.option2, formData.option3, formData.option4],
          correctAnswerIndex: formData.correctAnswerIndex,
          difficulty: formData.difficulty,
          category: formData.category,
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSnackbar({ open: true, message: 'Soru başarıyla eklendi!', severity: 'success' });
      setAddDialogOpen(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      console.error('Error adding question:', error);
      setSnackbar({ open: true, message: 'Soru eklenirken hata oluştu', severity: 'error' });
    }
  };

  const handleEditQuestion = async () => {
    if (!selectedQuestion) return;

    if (!formData.question || !formData.option1 || !formData.option2 || !formData.option3 || !formData.option4) {
      setSnackbar({ open: true, message: 'Lütfen tüm alanları doldurun', severity: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');

      await axios.put(
        `${API_BASE_URL}/api/vs/questions/${selectedQuestion.id}`,
        {
          question: formData.question,
          options: [formData.option1, formData.option2, formData.option3, formData.option4],
          correctAnswerIndex: formData.correctAnswerIndex,
          difficulty: formData.difficulty,
          category: formData.category,
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSnackbar({ open: true, message: 'Soru başarıyla güncellendi!', severity: 'success' });
      setEditDialogOpen(false);
      setSelectedQuestion(null);
      resetForm();
      fetchQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      setSnackbar({ open: true, message: 'Soru güncellenirken hata oluştu', severity: 'error' });
    }
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      const token = localStorage.getItem('adminToken');

      await axios.delete(
        `${API_BASE_URL}/api/vs/questions/${questionToDelete.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSnackbar({ open: true, message: 'Soru başarıyla silindi!', severity: 'success' });
      setDeleteDialogOpen(false);
      setQuestionToDelete(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      setSnackbar({ open: true, message: 'Soru silinirken hata oluştu', severity: 'error' });
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      setSnackbar({ open: true, message: 'Lütfen soru verisi girin', severity: 'error' });
      return;
    }

    setBulkImportLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const questionsToImport = JSON.parse(bulkImportText);

      if (!Array.isArray(questionsToImport)) {
        throw new Error('Veri bir dizi olmalı');
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/vs/questions/bulk`,
        { questions: questionsToImport },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const { successCount, skippedCount, errorCount } = response.data.data;
        setSnackbar({
          open: true,
          message: `${successCount} soru eklendi, ${skippedCount} soru zaten mevcut (atlandı), ${errorCount} hata`,
          severity: successCount > 0 ? 'success' : 'error'
        });

        if (response.data.data.successCount > 0) {
          setBulkImportDialogOpen(false);
          setBulkImportText('');
          fetchQuestions();
        }
      }
    } catch (error) {
      console.error('Error parsing bulk import:', error);
      setSnackbar({ open: true, message: 'JSON formatı hatalı veya sunucu hatası', severity: 'error' });
    } finally {
      setBulkImportLoading(false);
    }
  };

  const handleTriggerAI = async () => {
    try {
      setAiLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/trigger-daily-questions`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Yapay Zeka soruları başarıyla üretti!',
          severity: 'success'
        });
        // LİSTEYİ OTOMATİK YENİLE
        fetchQuestions();
      }
    } catch (error) {
      console.error('AI trigger error:', error);
      setSnackbar({ open: true, message: 'Yapay Zeka tetiklenirken hata oluştu', severity: 'error' });
    } finally {
      setAiLoading(false);
    }
  };

  const exportQuestions = () => {
    const exportData = questions.map(q => ({
      question: q.question,
      options: q.options,
      correctAnswerIndex: q.correctAnswerIndex,
      difficulty: q.difficulty,
      category: q.category,
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vs_questions_export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({
      question: '',
      option1: '',
      option2: '',
      option3: '',
      option4: '',
      correctAnswerIndex: 0,
      difficulty: 'medium',
      category: 'general',
    });
  };

  const openEditDialog = (question: VSQuestion) => {
    setSelectedQuestion(question);
    setFormData({
      question: question.question,
      option1: question.options[0] || '',
      option2: question.options[1] || '',
      option3: question.options[2] || '',
      option4: question.options[3] || '',
      correctAnswerIndex: question.correctAnswerIndex,
      difficulty: question.difficulty,
      category: question.category,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (question: VSQuestion) => {
    setQuestionToDelete(question);
    setDeleteDialogOpen(true);
  };

  // Filter questions
  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.question.toLowerCase().includes(search.toLowerCase()) ||
      q.options.some(opt => opt.toLowerCase().includes(search.toLowerCase()));
    const matchesDifficulty = difficultyFilter === 'all' || q.difficulty === difficultyFilter;
    const matchesCategory = categoryFilter === 'all' || q.category === categoryFilter;
    return matchesSearch && matchesDifficulty && matchesCategory;
  });

  const paginatedQuestions = filteredQuestions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Kolay';
      case 'medium': return 'Orta';
      case 'hard': return 'Zor';
      default: return difficulty;
    }
  };

  const getCategoryLabel = (category: string) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  // Stats
  const stats = {
    total: questions.length,
    unused: questions.filter(q => q.timesAsked === 0).length,
    easy: questions.filter(q => q.difficulty === 'easy').length,
    medium: questions.filter(q => q.difficulty === 'medium').length,
    hard: questions.filter(q => q.difficulty === 'hard').length,
    avgCorrectRate: questions.length > 0
      ? (questions.reduce((acc, q) => acc + (q.correctAnswerRate || 0), 0) / questions.length).toFixed(1)
      : 0,
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#8B4513' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Bolt sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            VS Kapışma Soru Yönetimi
          </Typography>
          <IconButton color="inherit" onClick={fetchQuestions}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
          <Card sx={{ p: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="h4" color="primary">{stats.total}</Typography>
            <Typography variant="body2" color="textSecondary">Toplam Soru</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#e8f5e9' }}>
            <Typography variant="h4" color="success.main">{stats.unused}</Typography>
            <Typography variant="body2" color="textSecondary">Kullanılmamış</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#e3f2fd' }}>
            <Typography variant="h4" color="info.main">{stats.easy}</Typography>
            <Typography variant="body2" color="textSecondary">Kolay</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#fff8e1' }}>
            <Typography variant="h4" color="warning.main">{stats.medium}</Typography>
            <Typography variant="body2" color="textSecondary">Orta</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#ffebee' }}>
            <Typography variant="h4" color="error">{stats.hard}</Typography>
            <Typography variant="body2" color="textSecondary">Zor</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#f3e5f5' }}>
            <Typography variant="h4" color="secondary">%{stats.avgCorrectRate}</Typography>
            <Typography variant="body2" color="textSecondary">Ort. Doğru</Typography>
          </Card>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Sorular" />
            <Tab label="İstatistikler" />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          {/* Search and Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                placeholder="Soru ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Zorluk</InputLabel>
                <Select
                  value={difficultyFilter}
                  label="Zorluk"
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="easy">Kolay</MenuItem>
                  <MenuItem value="medium">Orta</MenuItem>
                  <MenuItem value="hard">Zor</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Kategori</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Kategori"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AutoAwesome />}
                  onClick={handleTriggerAI}
                  disabled={aiLoading}
                  sx={{
                    background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                    color: 'white',
                    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                  }}
                >
                  {aiLoading ? 'Başlatılıyor...' : 'Yapay Zeka ile Üret'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={() => setBulkImportDialogOpen(true)}
                >
                  İçe Aktar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={exportQuestions}
                >
                  Dışa Aktar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    resetForm();
                    setAddDialogOpen(true);
                  }}
                  sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#704010' }, whiteSpace: 'nowrap' }}
                >
                  Yeni Soru
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* AI Job Status Info */}
          {jobStatus && (
            <Alert
              severity={jobStatus.status === 'error' ? 'error' : 'info'}
              sx={{ mb: 3, bgcolor: '#fff', border: '1px solid #e0e0e0' }}
              icon={<AutoAwesome />}
            >
              <strong>Yapay Zeka Durumu:</strong> {jobStatus.status === 'success' ? 'Tamamlandı' : jobStatus.status === 'active' ? 'Soru Üretiyor...' : 'Sırada Bekliyor'}
              {' | '}
              <strong>Bugün Üretilen:</strong> {jobStatus.questionsAdded || 0} / {jobStatus.target || 200}
              {jobStatus.lastRunTime && (
                <>
                  {' | '}
                  <strong>Son Çalışma:</strong> {new Date(jobStatus.lastRunTime._seconds * 1000).toLocaleString('tr-TR')}
                </>
              )}
            </Alert>
          )}

          {/* Questions Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Soru</strong></TableCell>
                  <TableCell><strong>Şıklar</strong></TableCell>
                  <TableCell><strong>Doğru Cevap</strong></TableCell>
                  <TableCell><strong>Zorluk</strong></TableCell>
                  <TableCell><strong>Kategori</strong></TableCell>
                  <TableCell><strong>Kullanım</strong></TableCell>
                  <TableCell><strong>Doğru %</strong></TableCell>
                  <TableCell align="right"><strong>İşlemler</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <TableCell key={i}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedQuestions.length > 0 ? (
                  paginatedQuestions.map((question) => (
                    <TableRow key={question.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {question.question}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {question.options.map((opt, idx) => (
                            <Typography
                              key={idx}
                              variant="caption"
                              sx={{
                                color: idx === question.correctAnswerIndex ? 'success.main' : 'text.secondary',
                                fontWeight: idx === question.correctAnswerIndex ? 'bold' : 'normal'
                              }}
                            >
                              {String.fromCharCode(65 + idx)}. {opt.substring(0, 25)}{opt.length > 25 ? '...' : ''}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<CheckCircle />}
                          label={String.fromCharCode(65 + question.correctAnswerIndex)}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getDifficultyLabel(question.difficulty)}
                          color={getDifficultyColor(question.difficulty)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getCategoryLabel(question.category)}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${question.timesAsked || 0}x`}
                          color={question.timesAsked === 0 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(question.correctAnswerRate || 0) * 100}
                            sx={{ width: 50, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            %{((question.correctAnswerRate || 0) * 100).toFixed(0)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditDialog(question)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(question)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Soru bulunamadı
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredQuestions.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Statistics Tab */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Category Distribution */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Kategori Dağılımı</Typography>
                {categories.map(cat => {
                  const count = questions.filter(q => q.category === cat.value).length;
                  const percentage = questions.length > 0 ? (count / questions.length * 100).toFixed(1) : 0;
                  return (
                    <Box key={cat.value} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{cat.label}</Typography>
                        <Typography variant="body2">{count} soru (%{percentage})</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Number(percentage)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>

            {/* Most Asked Questions */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>En Çok Sorulan Sorular</Typography>
                {questions
                  .sort((a, b) => (b.timesAsked || 0) - (a.timesAsked || 0))
                  .slice(0, 5)
                  .map((q, idx) => (
                    <Box key={q.id} sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {idx + 1}. {q.question.substring(0, 50)}{q.question.length > 50 ? '...' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={`${q.timesAsked || 0}x soruldu`} size="small" />
                        <Chip label={`%${((q.correctAnswerRate || 0) * 100).toFixed(0)} doğru`} size="small" color="success" />
                      </Box>
                    </Box>
                  ))}
              </CardContent>
            </Card>

            {/* Hardest Questions */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>En Zor Sorular (Düşük Doğru Oranı)</Typography>
                {questions
                  .filter(q => (q.timesAsked || 0) >= 5)
                  .sort((a, b) => (a.correctAnswerRate || 0) - (b.correctAnswerRate || 0))
                  .slice(0, 5)
                  .map((q, idx) => (
                    <Box key={q.id} sx={{ mb: 2, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {idx + 1}. {q.question.substring(0, 50)}{q.question.length > 50 ? '...' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={`%${((q.correctAnswerRate || 0) * 100).toFixed(0)} doğru`} size="small" color="error" />
                        <Chip label={getDifficultyLabel(q.difficulty)} size="small" />
                      </Box>
                    </Box>
                  ))}
                {questions.filter(q => (q.timesAsked || 0) >= 5).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Henüz yeterli veri yok (min 5 kere sorulmuş soru gerekli)
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Easiest Questions */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>En Kolay Sorular (Yüksek Doğru Oranı)</Typography>
                {questions
                  .filter(q => (q.timesAsked || 0) >= 5)
                  .sort((a, b) => (b.correctAnswerRate || 0) - (a.correctAnswerRate || 0))
                  .slice(0, 5)
                  .map((q, idx) => (
                    <Box key={q.id} sx={{ mb: 2, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {idx + 1}. {q.question.substring(0, 50)}{q.question.length > 50 ? '...' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={`%${((q.correctAnswerRate || 0) * 100).toFixed(0)} doğru`} size="small" color="success" />
                        <Chip label={getDifficultyLabel(q.difficulty)} size="small" />
                      </Box>
                    </Box>
                  ))}
                {questions.filter(q => (q.timesAsked || 0) >= 5).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Henüz yeterli veri yok (min 5 kere sorulmuş soru gerekli)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </TabPanel>
      </Box>

      {/* Add Question Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yeni VS Sorusu Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Soru"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
              placeholder="Örn: Hangi ülke en fazla kahve üretmektedir?"
            />

            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend">Şıklar (Doğru cevabı seçin)</FormLabel>
              <RadioGroup
                value={formData.correctAnswerIndex}
                onChange={(e) => setFormData({ ...formData, correctAnswerIndex: parseInt(e.target.value) })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel value={0} control={<Radio />} label="A" />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Şık A"
                    value={formData.option1}
                    onChange={(e) => setFormData({ ...formData, option1: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel value={1} control={<Radio />} label="B" />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Şık B"
                    value={formData.option2}
                    onChange={(e) => setFormData({ ...formData, option2: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel value={2} control={<Radio />} label="C" />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Şık C"
                    value={formData.option3}
                    onChange={(e) => setFormData({ ...formData, option3: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel value={3} control={<Radio />} label="D" />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Şık D"
                    value={formData.option4}
                    onChange={(e) => setFormData({ ...formData, option4: e.target.value })}
                  />
                </Box>
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                select
                label="Zorluk"
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
              >
                <MenuItem value="easy">Kolay</MenuItem>
                <MenuItem value="medium">Orta</MenuItem>
                <MenuItem value="hard">Zor</MenuItem>
              </TextField>

              <TextField
                fullWidth
                select
                label="Kategori"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>İptal</Button>
          <Button onClick={handleAddQuestion} variant="contained" color="primary">
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Soruyu Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Soru"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />

            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend">Şıklar (Doğru cevabı seçin)</FormLabel>
              <RadioGroup
                value={formData.correctAnswerIndex}
                onChange={(e) => setFormData({ ...formData, correctAnswerIndex: parseInt(e.target.value) })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel value={0} control={<Radio />} label="A" />
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.option1}
                    onChange={(e) => setFormData({ ...formData, option1: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel value={1} control={<Radio />} label="B" />
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.option2}
                    onChange={(e) => setFormData({ ...formData, option2: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel value={2} control={<Radio />} label="C" />
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.option3}
                    onChange={(e) => setFormData({ ...formData, option3: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel value={3} control={<Radio />} label="D" />
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.option4}
                    onChange={(e) => setFormData({ ...formData, option4: e.target.value })}
                  />
                </Box>
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                select
                label="Zorluk"
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
              >
                <MenuItem value="easy">Kolay</MenuItem>
                <MenuItem value="medium">Orta</MenuItem>
                <MenuItem value="hard">Zor</MenuItem>
              </TextField>

              <TextField
                fullWidth
                select
                label="Kategori"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleEditQuestion} variant="contained" color="primary">
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Soruyu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu soruyu silmek istediğinize emin misiniz?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            "{questionToDelete?.question}"
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteQuestion} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkImportDialogOpen} onClose={() => setBulkImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Toplu Soru İçe Aktarma</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            JSON formatında sorular yapıştırın. Her soru şu alanları içermelidir:
            <br />
            - question: Soru metni
            <br />
            - options: 4 şıklık dizi
            <br />
            - correctAnswerIndex: Doğru cevap indeksi (0-3)
            <br />
            - difficulty: "easy", "medium" veya "hard" (opsiyonel)
            <br />
            - category: Kategori kodu (opsiyonel)
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={15}
            placeholder={`[
  {
    "question": "Hangi ülke en fazla kahve üretir?",
    "options": ["Kolombiya", "Brezilya", "Vietnam", "Etiyopya"],
    "correctAnswerIndex": 1,
    "difficulty": "medium",
    "category": "coffee"
  }
]`}
            value={bulkImportText}
            onChange={(e) => setBulkImportText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkImportDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleBulkImport}
            variant="contained"
            color="primary"
            disabled={bulkImportLoading}
          >
            {bulkImportLoading ? 'İçe Aktarılıyor...' : 'İçe Aktar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VSQuestionsManagementPage;
